import { ArrowType, AssetType, LineType, Triple } from './apiTypes';
import { ArdoqId, Persisted } from './coreTypes';

export type MetaModel = {
  componentTypes: MetaModelComponentType[];
  referenceTypes: MetaModelReferenceType[];
  triples: MetaModelTriple[];
};

export type MetaModelComponentType = {
  name: string;
  parentName?: string;
  style: {
    color: string | null;
    icon: string | null;
    image: string | null;
    level: number; // defines the color if none is set
    shape: string | null;
    standard: string | null;
  };
  usedInWorkspaceIds: ArdoqId[];
  fieldNames: string[];
};

export type MetaModelReferenceType = {
  name: string;
  style: {
    color: string;
    line: LineType;
    lineBeginning?: ArrowType;
    lineEnding: ArrowType;
    svgStyle: string | null;
    hasCardinality?: boolean;
  };
  usedInWorkspaceIds: ArdoqId[];
  fieldNames: string[];
};

export type MetaModelTriple = {
  sourceComponentType: string;
  referenceType: string;
  targetComponentType: string;
};

export enum UseCaseConfigIds {
  ApplicationLifecycleManagement = 'ApplicationLifecycleManagement',
  ApplicationIntegrationManagement = 'ApplicationIntegrationManagement',
  ApplicationHosting = 'ApplicationHosting',
  BusinessCapabilityModeling = 'BusinessCapabilityModeling',
  BusinessCapabilityRealization = 'BusinessCapabilityRealization',
  ITLifecycleManagement = 'ITLifecycleManagement',
  TechnicalCapabilityManagementRealization = 'TechnicalCapabilityManagementRealization',
  ITCostManagement = 'ITCostManagement',
  ApplicationRationalization = 'ApplicationRationalization',
  DataLineage = 'DataLineage',
  CloudMigration = 'CloudMigration',
  StrategyToExecution = 'StrategyToExecution',
  TechnologyPortfolioManagement = 'TechnologyPortfolioManagement',
  ApplicationRiskManagement = 'ApplicationRiskManagement',
}

export type UseCaseKBLink = {
  label: string;
  link: string;
};

export type UseCaseConfig = {
  _id: UseCaseConfigIds;
  name: string;
  KBLinks: UseCaseKBLink[];
  description: string;
};

export enum UseCaseStatus {
  IN_PROGRESS = 'inProgress',
  PENDING = 'pending',
}

export type Augmentation = {
  displayName: string;
  id: string;
  enabled: boolean;
  disabledReason?: string;
  completed: boolean;
  settings: AugmentationAPISettings;
};

type AugmentationAPISettings = {
  [key: string]: {
    displayName: string;
    selected: string;
    options: Array<{
      name: string;
      _id: string;
    }>;
  };
};

export type UseCaseSummary = {
  _id: ArdoqId;
  name: string;
  ardoq: { entityType: 'use-case' };
  status: UseCaseStatus;
  updateAvailable: boolean;
  iteration?: string;
  latestIteration?: string;
  description: string | null;
  category?: string;
  augmentations?: Augmentation[];
};

export enum VisibilityUseCase {
  ALM = 'ALM - Application Lifecycle Management',
  OM = 'OM - Organizational Modeling',
  BCM = 'BCM - Business Capability Modeling',
  BCR = 'BCR - Business Capability Realization',
  F = 'Foundation',
  AR = 'AR - Application Rationalization',
  AH = 'AH - Application Hosting',
  ITLM = 'ITLM - Infrastructure Technology Lifecycle Management',
  TCM = 'TCM - Technical Capability Modeling and Realization',
  ITCM = 'ITCM - IT Cost Management',
  DL = 'DL - Data Lineage',
  BPM = 'BPM - Business Process Management',
  BVS = 'BVS - Business Value Streams',
  AIM = 'AIM - Application Integration Management',
  BHC = 'BHC - Business Health Check',
  EAIM = 'EAIM - Enterprise AI Management',
}

export enum TransformationUseCase {
  S2E = 'S2E - Strategy to Execution',
  CM = 'CM - Cloud Migration',
  CBP = 'CBP - Capability Based Planning',
}

export enum OversightUseCase {
  ARM = 'ARM - Application Risk Management',
  CA = 'CA - Compliance Assurance',
  REC = 'REC - Architecture Records',
  TDM = 'TDM - Technical Debt Management',
  TPM = 'TPM - Technology Portfolio Management',
  REG = 'REG - Regulatory Compliance',
  SHC = 'SHC - Solution Health Checks',
  EAIG = 'EAIG - Enterprise AI Governance',
  DR = 'DR - Disaster Recovery',
}

export type APIMetaModelPreview = MetaModel & {
  componentTypeFieldsAndTriples: ComponentTypeRelatedFieldsAndTriples[];
};

export type ComponentTypeRelatedFieldsAndTriples = {
  label: string;
  fields: Array<string>;
  metamodelTriples: Array<TripleWithRelatedFields>;
};

export type TripleWithRelatedFields = {
  triple: Triple;
  fields: Array<string>;
};

export enum UseCaseSpecificAssetType {
  SERVICENOW_CONFIG = 'servicenowConfig',
  TABULAR_MAPPING = 'tabularMapping',
  PERSPECTIVE = 'perspective',
}

export type UseCaseAssetType = AssetType | UseCaseSpecificAssetType;
export type UseCaseContent = { name: string; id: ArdoqId };

type UseCase = {
  name: string;
  description?: string;
  iteration: string;
  category?: string;
  businessOutcomes: string;
  metamodelTriples: Triple[];
  entityGroupId: ArdoqId;
  changeLogLink: string;
  pdfLinks: PDFLink[];
  publicPresentationLinks: PDFLink[];
  keyMetricsDashboardId: ArdoqId;
  contentByType: Partial<Record<UseCaseAssetType, Array<UseCaseContent>>>;
  augmentations?: Augmentation[];
};

export type PDFLink = {
  label: string;
  link: string;
};

export type PersistedUseCase = Persisted<UseCase>;
